// =============================================================================
// api.js — fetch wrapper that auto-attaches the Supabase JWT
// =============================================================================

import { supabase } from './supabase.js';

const API_BASE = import.meta.env.VITE_API_URL || '';    // empty = use Vite proxy (/api → backend)

async function getAuthHeader() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

// =============================================================================
// THE TIMEOUT (S101, cw_271 §6 / cw_272)
// =============================================================================
// 🔴 THE DEFECT THIS CLOSES: every fetch here was unbounded. A hung
// /gmail-verify left the Connect card with no buttons and no way out — the
// server bounds Google at 30 s, but nothing bounded the BROWSER, so a request
// that never settled left the UI awaiting a promise that never resolved.
//
// ⚠ WHERE 45 s ACTUALLY COMES FROM — READ THIS BEFORE CHANGING IT.
// It is the server's OUTBOUND call ceiling plus headroom, and NOTHING MORE.
// Every external call the server makes is AbortSignal.timeout(30_000) —
// gmail.js x2, flux-campaigns.js x2, flux-oauth.js, flux-variants.js, and
// ANTHROPIC_TIMEOUT_MS in flux-digest-compose.js. 45_000 = that 30 s + 15 s for
// TLS, Railway queueing and cold start.
//
// 🔴 A 30 s BOUND ON EACH OUTBOUND CALL IS NOT A 30 s BOUND ON A HANDLER. A
// route that makes a 30 s Gmail call, writes rows, then makes a second call is
// over 30 s of server work while every individual call sits comfortably inside
// its own bound. NO END-TO-END HANDLER DURATION WAS EVER MEASURED. The 15 s of
// headroom is assumed to absorb that gap; it is not known to.
//
// So do not read "sized against the server's ceiling" as stronger than it is,
// and do not re-tune this number from theory. TIMEOUT_ABORTS below counts real
// aborts by path — a month of real use will say whether 45 s is wrong and
// exactly where, which is worth more than any amount of reasoning here.
const DEFAULT_TIMEOUT_MS = 45_000;

// Observability, per cw_272 §1: instrument, do not pre-tune. Every abort is
// counted against the path that aborted, so the answer to "is the timeout
// wrong" is a lookup rather than an argument. Deliberately a plain in-memory
// tally — it survives no reload and is meant for a console read during a
// session, not for analytics.
export const TIMEOUT_ABORTS = new Map();

function recordAbort(path, ms) {
  TIMEOUT_ABORTS.set(path, (TIMEOUT_ABORTS.get(path) || 0) + 1);
  console.warn(`[api] timeout after ${ms}ms: ${path} (aborts on this path: ${TIMEOUT_ABORTS.get(path)})`);
}

// A timeout must be distinguishable from a 500 WITHOUT string-matching a
// message. `err.timeout === true` is set only by "no response arrived", so no
// server response can forge it; callers branch on the boolean. 408 rides along
// for anything already switching on status, and the message is a plain sentence
// because existing catch blocks surface err.message directly.
function timeoutError(path, ms) {
  const err = new Error("The server didn't respond in time. Check your connection and try again.");
  err.timeout = true;
  err.status = 408;
  err.path = path;
  return err;
}

export async function api(path, options = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
  const authHeader = await getAuthHeader();
  const headers = {
    'Content-Type': 'application/json',
    ...authHeader,
    ...(fetchOptions.headers || {}),
  };

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  // AbortController rather than AbortSignal.timeout() so a caller's own signal
  // still composes, and so the abort reason is ours to identify.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetch(url, { ...fetchOptions, headers, signal: controller.signal });
  } catch (e) {
    if (controller.signal.aborted) {
      recordAbort(path, timeoutMs);
      throw timeoutError(path, timeoutMs);
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }

  let body;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body;
}

// Convenience methods. Each forwards an options bag so a caller can pass
// `{ timeoutMs }` without dropping down to api() directly.
api.get = (path, options = {}) => api(path, { ...options, method: 'GET' });
api.post = (path, body, options = {}) => api(path, { ...options, method: 'POST', body: JSON.stringify(body) });
api.put = (path, body, options = {}) => api(path, { ...options, method: 'PUT', body: JSON.stringify(body) });
api.del = (path, options = {}) => api(path, { ...options, method: 'DELETE' });

// P9a W-7 (T-1): the flux routes are PATCH-heavy (/:id, /:id/archive,
// /:id/unarchive) and callers were spelling PATCH raw at every site.
api.patch = (path, body, options = {}) => api(path, { ...options, method: 'PATCH', body: JSON.stringify(body) });

// P9a W-7 / adjudication D-19 — multipart upload. Closes ledger G-10.
//
// It cannot route through api() above, which hard-sets
// 'Content-Type: application/json'. A multipart body needs the BROWSER to set
// the header so it can attach the boundary; setting it by hand produces a
// request the server cannot parse. So: same JWT, same error shape, no forced
// content type, and no JSON size cap (express.json's 1 MB limit does not apply
// to multipart, which is the entire point — T-2).
//
// Built here and NOT called by P9a. P9b W-3's contact import over the P8
// importer is the consumer; it lives here because P9a owns api.js in this
// range and a second wrapper written later is a second wrapper.
// 🔴 THIS DOES NOT ROUTE THROUGH api() ABOVE, SO IT NEEDS ITS OWN TIMEOUT.
// That is the trap in this file: a timeout added to api() alone silently misses
// every file upload, and uploads are the one call class that legitimately runs
// long.
//
// 120 s, not the 45 s default and NOT unbounded. The cap is 10 MB and the
// binding constraint is the UPLINK, not server work — a kid on a bad phone
// connection legitimately exceeds 45 s. ⚠ But "no timeout" would just move the
// wedge here, to the child-safety photo route, which is the worst place to
// leave an unbounded spinner. Bounded, with an honest message.
const UPLOAD_TIMEOUT_MS = 120_000;

api.upload = async (path, formData, options = {}) => {
  const { timeoutMs = UPLOAD_TIMEOUT_MS } = options;
  const authHeader = await getAuthHeader();
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetch(url, {
      method: 'POST', headers: { ...authHeader }, body: formData, signal: controller.signal,
    });
  } catch (e) {
    if (controller.signal.aborted) {
      recordAbort(path, timeoutMs);
      throw timeoutError(path, timeoutMs);
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }

  let body;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const err = new Error(body?.error || `Upload failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body;
};
